import { useQuery } from "@apollo/client";
import { useUrlState, wrapClick } from "utils";
import { FinancialStatementView } from "components/cards";
import { SearchSelectInput } from "components/core";
import {
  CustomPicker,
  DayPicker,
  MonthPicker,
  QuarterPicker,
  WeekPicker,
} from "components";
import { Loader } from "components";
import _ from "lodash";
import { useMemo, useRef, useState, type FC } from "react";
import { useReactToPrint } from "react-to-print";
import { GET_SERVICE_POINT_TRANSACTIONS } from "../apollo";

const defaultViews = ["all-time", "day", "week", "month", "custom"] as const;

interface StatementsContainerProps {
  servicePoint: any;
}

export const StatementsContainer: FC<StatementsContainerProps> = ({
  servicePoint,
}) => {
  const [periodType, setPeriodType] = useState<string>("month");
  const [fromDate] = useUrlState("fromDate");
  const [toDate] = useUrlState("toDate");

  const filter = useMemo(() => {
    if (periodType === "all-time") {
      return {
        servicePoint: servicePoint?._id,
      };
    }
    return {
      fromDate,
      toDate,
      servicePoint: servicePoint?._id,
    };
  }, [periodType, fromDate, toDate, servicePoint?._id]);

  const { data, loading } = useQuery(GET_SERVICE_POINT_TRANSACTIONS, {
    variables: filter,
    fetchPolicy: "network-only",
    skip: !servicePoint,
  });
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Statement of Account for ${servicePoint?.code}`,
    bodyClass: "h-auto w-[1000px] m-0 scale-100 transform",
  });    

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <section className="flex-1 flex flow-root overflow-y-auto">
        <div className="flex justify-end mt-5 px-[30px] gap-4">
          <div className="">
            <SearchSelectInput
              id="periodType"
              options={defaultViews.map((view) => ({
                label: { title: _.upperFirst(view) },
                value: view,
              }))}
              values={{ periodType }}
              setFieldValue={(_field: string, value: string) => {
                setPeriodType(value);
              }}
              label={"Period Type"}
              placeholder="Select Period Type"
              position="bottom"
            />
          </div>
          {periodType && periodType !== "all-time" && (
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Period
              </label>
              <div className="mt-1">
                {periodType === "day" && <DayPicker />}
                {periodType === "week" && <WeekPicker />}
                {periodType === "month" && <MonthPicker />}
                {periodType === "quarter" && <QuarterPicker />}
                {periodType === "custom" && <CustomPicker />}
              </div>
            </div>
          )}
        </div>
        {/* <div className='absolute inset-0 -z-10 bg-[radial-gradient(45rem_50rem_at_top,theme(colors.red.100),white)] opacity-20' />
      <div className='absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] bg-white shadow-xl shadow-red-600/10 ring-1 ring-red-50 sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center' /> */}
        {!loading ? (
          <>
            {data?.transactions?.length > 0 ? (
              <div>
                <FinancialStatementView
                  ref={printRef}
                  transactions={data?.transactions}
                  servicePoint={servicePoint}
                  fromDate={fromDate}
                  toDate={toDate}
                />
              </div>
            ) : (
              <div className="flex min-h-[30vh] items-center justify-center">
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      vectorEffect="non-scaling-stroke"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    No Statement Data
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    This customer has no statement data for the selected period
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[30vh]">
            <Loader />
          </div>
        )}
      </section>
      {data?.transactions?.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
          <button
            type="button"
            onClick={wrapClick(handlePrint)}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Print
          </button>
        </div>
      )}
    </div>
  );
};
